
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../api/api";
import dayjs from "dayjs";

import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    RefreshRounded,
    SearchRounded,
    GroupsRounded,
    PersonRounded,
    CheckCircleRounded,
    CancelRounded,
    HourglassBottomRounded,
    EmojiEventsRounded,
    VisibilityRounded,
    AccessTimeRounded,
    QuizRounded,
    TrendingUpRounded,
    RadioButtonCheckedRounded,
    ScheduleRounded,
    FactCheckRounded,
    SignalCellularAltRounded,
} from "@mui/icons-material";

const LIVE_MONITOR_ENDPOINT = "/api/liveentrancemonitor/";
const REFRESH_INTERVAL = 5000;

const COLORS = {
    navy: "#14283F",
    navyLight: "#203F5F",
    green: "#15956B",
    greenLight: "#E8F7F0",
    gold: "#E8B84A",
    background: "#F4F6F9",
    border: "#E6EAF0",
    text: "#1D2939",
    muted: "#7C8798",
    white: "#FFFFFF",
    red: "#D64545",
    redLight: "#FDECEC",
    blue: "#2875C7",
    blueLight: "#EAF3FF",
    orange: "#D97706",
    orangeLight: "#FFF5E5",
};

const SUBJECTS = [
    { value: "english", label: "English" },
    { value: "mathematics", label: "Mathematics" },
];

const ANSWER_OPTIONS = ["A", "B", "C", "D"];

const numberValue = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
};

const firstAvailable = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return null;
};

const normalizeText = (value) =>
    String(value ?? "").trim().toLowerCase();

const formatNumber = (value, decimals = 0) => {
    const parsed = numberValue(value);

    return parsed.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

const formatDateTime = (value) => {
    if (!value) {
        return "Not available";
    }

    const parsed = dayjs(value);

    return parsed.isValid()
        ? parsed.format("DD MMM YYYY, hh:mm:ss A")
        : String(value);
};

const formatTime = (value) => {
    if (!value) {
        return "Not available";
    }

    const parsed = dayjs(value);

    return parsed.isValid()
        ? parsed.format("hh:mm:ss A")
        : String(value);
};

const formatDuration = (seconds) => {
    const total = Math.max(0, Math.floor(numberValue(seconds)));

    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainingSeconds = total % 60;

    if (hours > 0) {
        return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(
            remainingSeconds
        ).padStart(2, "0")}s`;
    }

    return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
};

const getApplicantName = (applicant) => {
    const directName = firstAvailable(
        applicant?.applicant_name,
        applicant?.full_name,
        applicant?.student_name,
        applicant?.name,
        applicant?.application?.full_name,
        applicant?.application?.applicant_name
    );

    if (directName) {
        return String(directName).trim();
    }

    const firstName = firstAvailable(
        applicant?.first_name,
        applicant?.application?.first_name
    );

    const lastName = firstAvailable(
        applicant?.last_name,
        applicant?.application?.last_name
    );

    const combined = [firstName, lastName].filter(Boolean).join(" ").trim();

    return combined || "Unknown Applicant";
};

const getApplicationNumber = (applicant) =>
    String(
        firstAvailable(
            applicant?.application_number,
            applicant?.entrance_application_number,
            applicant?.application?.application_number,
            applicant?.application?.entrance_application_number
        ) ?? "No application number"
    );

const getAttemptId = (applicant) =>
    firstAvailable(
        applicant?.attempt_id,
        applicant?.exam_attempt_id,
        applicant?.attempt?.id,
        applicant?.id
    );

const getApplicantKey = (applicant) => {
    const attemptId = getAttemptId(applicant);
    const applicationNumber = getApplicationNumber(applicant);

    return `${attemptId ?? "no-attempt"}-${applicationNumber}`;
};

const getApplicantAnswers = (applicant) => {
    const answerData = firstAvailable(
        applicant?.answers,
        applicant?.answered_questions,
        applicant?.answer_details,
        applicant?.responses,
        applicant?.attempt?.answers
    );

    if (Array.isArray(answerData)) {
        return answerData;
    }

    if (answerData && typeof answerData === "object") {
        return Object.entries(answerData).map(([key, value]) => {
            if (value && typeof value === "object") {
                return {
                    question_number: key,
                    ...value,
                };
            }

            return {
                question_number: key,
                selected_answer: value,
            };
        });
    }

    return [];
};

const getSelectedAnswer = (answer) =>
    firstAvailable(
        answer?.selected_answer,
        answer?.chosen_answer,
        answer?.answer,
        answer?.selected_option,
        answer?.response,
        answer?.choice
    );

const getCorrectAnswer = (answer) =>
    firstAvailable(
        answer?.correct_answer,
        answer?.expected_answer,
        answer?.answer_key,
        answer?.question?.correct_answer
    );

const getQuestionNumber = (answer, index) =>
    firstAvailable(
        answer?.question_number,
        answer?.number,
        answer?.question_no,
        answer?.question?.number,
        answer?.question?.question_number,
        index + 1
    );

const getQuestionText = (answer) =>
    firstAvailable(
        answer?.question_text,
        answer?.text,
        answer?.question_content,
        answer?.content,
        typeof answer?.question === "string" ? answer.question : null,
        answer?.question?.question_text,
        answer?.question?.text,
        answer?.question?.content
    ) ?? "Question text was not returned by the server.";

const getAnswerCorrectness = (answer) => {
    if (typeof answer?.is_correct === "boolean") {
        return answer.is_correct;
    }

    if (typeof answer?.correct === "boolean") {
        return answer.correct;
    }

    if (typeof answer?.is_correct === "number") {
        return answer.is_correct === 1;
    }

    if (typeof answer?.correct === "number") {
        return answer.correct === 1;
    }

    const selected = getSelectedAnswer(answer);
    const correct = getCorrectAnswer(answer);

    if (
        selected !== null &&
        selected !== undefined &&
        correct !== null &&
        correct !== undefined
    ) {
        return normalizeText(selected) === normalizeText(correct);
    }

    return null;
};

const getAnswerMarks = (answer) =>
    numberValue(
        firstAvailable(
            answer?.marks_awarded,
            answer?.awarded_marks,
            answer?.obtained_marks,
            answer?.marks_obtained,
            answer?.score
        )
    );

const getApplicantStats = (applicant) => {
    const answers = getApplicantAnswers(applicant);

    const answeredFromAnswers = answers.filter((answer) => {
        const selected = getSelectedAnswer(answer);

        return (
            selected !== null &&
            selected !== undefined &&
            String(selected).trim() !== ""
        );
    }).length;

    const totalQuestions = Math.max(
        0,
        numberValue(
            firstAvailable(
                applicant?.total_questions,
                applicant?.question_count,
                applicant?.exam?.question_count,
                applicant?.attempt?.total_questions
            )
        )
    );

    const reportedAnswered = numberValue(
        firstAvailable(
            applicant?.questions_answered,
            applicant?.answered_count,
            applicant?.answered_questions_count,
            applicant?.total_answered
        )
    );

    // Use the larger value so a stale counter cannot hide saved answer details.
    const questionsAnswered = Math.max(
        reportedAnswered,
        answeredFromAnswers
    );

    const correctFromAnswers = answers.filter(
        (answer) => getAnswerCorrectness(answer) === true
    ).length;

    const wrongFromAnswers = answers.filter(
        (answer) => getAnswerCorrectness(answer) === false
    ).length;

    const reportedCorrect = firstAvailable(
        applicant?.correct_answers_so_far,
        applicant?.correct_answers,
        applicant?.correct_count,
        applicant?.total_correct
    );

    const reportedWrong = firstAvailable(
        applicant?.wrong_answers_so_far,
        applicant?.wrong_answers,
        applicant?.wrong_count,
        applicant?.total_wrong
    );

    const correctAnswers =
        reportedCorrect !== null
            ? numberValue(reportedCorrect)
            : correctFromAnswers;

    const wrongAnswers =
        reportedWrong !== null
            ? numberValue(reportedWrong)
            : wrongFromAnswers;

    const totalMarks = Math.max(
        0,
        numberValue(
            firstAvailable(
                applicant?.total_marks,
                applicant?.exam_total_marks,
                applicant?.attempt?.total_marks
            )
        )
    );

    const calculatedMarks = answers.reduce(
        (total, answer) => total + getAnswerMarks(answer),
        0
    );

    const reportedMarks = firstAvailable(
        applicant?.obtained_marks,
        applicant?.marks_obtained,
        applicant?.score
    );

    const obtainedMarks =
        reportedMarks !== null
            ? numberValue(reportedMarks)
            : calculatedMarks;

    const reportedPercentage = firstAvailable(
        applicant?.percentage,
        applicant?.score_percentage
    );

    const scorePercentage =
        reportedPercentage !== null
            ? numberValue(reportedPercentage)
            : totalMarks > 0
              ? (obtainedMarks / totalMarks) * 100
              : 0;

    const unansweredReported = firstAvailable(
        applicant?.unanswered_questions,
        applicant?.unanswered_count
    );

    const unansweredQuestions =
        unansweredReported !== null
            ? Math.max(0, numberValue(unansweredReported))
            : Math.max(0, totalQuestions - questionsAnswered);

    const accuracy =
        questionsAnswered > 0
            ? (correctAnswers / questionsAnswered) * 100
            : 0;

    const completion =
        totalQuestions > 0
            ? Math.min(100, (questionsAnswered / totalQuestions) * 100)
            : 0;

    return {
        answers,
        questionsAnswered,
        correctAnswers,
        wrongAnswers,
        totalQuestions,
        unansweredQuestions,
        obtainedMarks,
        totalMarks,
        scorePercentage,
        accuracy,
        completion,
    };
};

const getApplicantStatus = (applicant) => {
    const status = normalizeText(
        firstAvailable(
            applicant?.status,
            applicant?.attempt_status,
            applicant?.attempt?.status
        )
    );

    if (
        applicant?.is_finalized === true ||
        applicant?.finalized === true ||
        status === "submitted" ||
        status === "completed"
    ) {
        return "submitted";
    }

    if (status === "expired") {
        return "expired";
    }

    if (status === "in_progress" || status === "in progress") {
        return "in_progress";
    }

    if (status === "not_started" || status === "not started") {
        return "not_started";
    }

    return status || "unknown";
};

const getStatusAppearance = (status) => {
    switch (status) {
        case "in_progress":
            return {
                label: "Sitting",
                color: COLORS.green,
                background: COLORS.greenLight,
                icon: <RadioButtonCheckedRounded fontSize="small" />,
            };

        case "submitted":
            return {
                label: "Submitted",
                color: COLORS.blue,
                background: COLORS.blueLight,
                icon: <CheckCircleRounded fontSize="small" />,
            };

        case "expired":
            return {
                label: "Expired",
                color: COLORS.red,
                background: COLORS.redLight,
                icon: <AccessTimeRounded fontSize="small" />,
            };

        case "not_started":
            return {
                label: "Not started",
                color: COLORS.muted,
                background: "#F0F2F5",
                icon: <ScheduleRounded fontSize="small" />,
            };

        default:
            return {
                label: status.replaceAll("_", " "),
                color: COLORS.muted,
                background: "#F0F2F5",
                icon: <VisibilityRounded fontSize="small" />,
            };
    }
};

const normalizeResponse = (data, fallbackSubject, fallbackDate) => {
    const leaderboard = Array.isArray(data?.leaderboard)
        ? data.leaderboard
        : Array.isArray(data?.applicants)
          ? data.applicants
          : Array.isArray(data?.results)
            ? data.results
            : [];

    return {
        date: data?.date || fallbackDate,
        subject: data?.subject || fallbackSubject,
        total_applicants:
            data?.total_applicants !== undefined &&
            data?.total_applicants !== null
                ? numberValue(data.total_applicants)
                : leaderboard.length,
        currently_sitting:
            data?.currently_sitting !== undefined &&
            data?.currently_sitting !== null
                ? numberValue(data.currently_sitting)
                : null,
        leader: data?.leader || leaderboard[0] || null,
        leaderboard,
        finalized: Boolean(data?.finalized),
        server_time: data?.server_time || null,
    };
};

const MetricCard = ({
    title,
    value,
    subtitle,
    icon,
    color = COLORS.navy,
    background = COLORS.white,
}) => (
    <Card
        elevation={0}
        sx={{
            height: "100%",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 3,
            background,
        }}
    >
        <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: COLORS.muted, fontWeight: 600 }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="h4"
                        sx={{
                            mt: 0.75,
                            fontWeight: 800,
                            color,
                            lineHeight: 1.15,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {value}
                    </Typography>

                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                mt: 0.75,
                                color: COLORS.muted,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Avatar
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2.5,
                        bgcolor: `${color}15`,
                        color,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Avatar>
            </Stack>
        </CardContent>
    </Card>
);

const SectionHeader = ({ icon, title, subtitle, action }) => (
    <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.25}
        sx={{ mb: 2 }}
    >
        <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
                sx={{
                    width: 42,
                    height: 42,
                    bgcolor: `${COLORS.navy}0D`,
                    color: COLORS.navy,
                    borderRadius: 2,
                }}
            >
                {icon}
            </Avatar>

            <Box>
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: COLORS.navy }}
                >
                    {title}
                </Typography>

                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Stack>

        {action}
    </Stack>
);

const AnswerValue = ({ label, value, correct, muted = false }) => (
    <Box
        sx={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            p: 1.25,
            minWidth: 0,
            background: muted ? "#F8FAFC" : COLORS.white,
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: COLORS.muted, display: "block", mb: 0.5 }}
        >
            {label}
        </Typography>

        <Typography
            variant="body2"
            sx={{
                fontWeight: 700,
                color:
                    correct === true
                        ? COLORS.green
                        : correct === false
                          ? COLORS.red
                          : COLORS.text,
                overflowWrap: "anywhere",
            }}
        >
            {value === null || value === undefined || value === ""
                ? "Not answered"
                : String(value)}
        </Typography>
    </Box>
);

export default function EntranceLiveMonitor() {
    const [subject, setSubject] = useState("english");
    const [selectedDate, setSelectedDate] = useState(
        dayjs().format("YYYY-MM-DD")
    );
    const [search, setSearch] = useState("");
    const [monitorData, setMonitorData] = useState({
        date: dayjs().format("YYYY-MM-DD"),
        subject: "english",
        total_applicants: 0,
        currently_sitting: null,
        leader: null,
        leaderboard: [],
        finalized: false,
        server_time: null,
    });

    const [selectedApplicantKey, setSelectedApplicantKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const mountedRef = useRef(false);
    const requestSequenceRef = useRef(0);
    const inFlightRef = useRef(false);
    const selectedApplicantKeyRef = useRef(null);

    const leaderboard = useMemo(() => {
        return Array.isArray(monitorData?.leaderboard)
            ? monitorData.leaderboard
            : [];
    }, [monitorData]);

    const loadMonitorData = useCallback(
        async (options = {}) => {
            const isManualRefresh = Boolean(options.manual);

            if (inFlightRef.current && !isManualRefresh) {
                return;
            }

            inFlightRef.current = true;

            const requestId = ++requestSequenceRef.current;

            if (mountedRef.current) {
                if (lastUpdated === null) {
                    setLoading(true);
                } else {
                    setRefreshing(true);
                }
            }

            try {
                const response = await api.get(LIVE_MONITOR_ENDPOINT, {
                    params: {
                        subject,
                        date: selectedDate,
                    },
                });

                if (
                    !mountedRef.current ||
                    requestId !== requestSequenceRef.current
                ) {
                    return;
                }

                const normalized = normalizeResponse(
                    response?.data,
                    subject,
                    selectedDate
                );

                setMonitorData(normalized);
                setError("");
                setLastUpdated(new Date().toISOString());

                const applicants = normalized.leaderboard;

                // Keep the selected applicant selected when polling updates arrive.
                const existingKey = selectedApplicantKeyRef.current;

                if (existingKey) {
                    const existingApplicant = applicants.find(
                        (applicant) => getApplicantKey(applicant) === existingKey
                    );

                    if (existingApplicant) {
                        setSelectedApplicantKey(existingKey);
                    } else if (applicants.length > 0) {
                        const nextKey = getApplicantKey(applicants[0]);

                        selectedApplicantKeyRef.current = nextKey;
                        setSelectedApplicantKey(nextKey);
                    } else {
                        selectedApplicantKeyRef.current = null;
                        setSelectedApplicantKey(null);
                    }
                } else if (applicants.length > 0) {
                    const nextKey = getApplicantKey(applicants[0]);

                    selectedApplicantKeyRef.current = nextKey;
                    setSelectedApplicantKey(nextKey);
                }
            } catch (requestError) {
                if (
                    !mountedRef.current ||
                    requestId !== requestSequenceRef.current
                ) {
                    return;
                }

                const responseData = requestError?.response?.data;

                const detail =
                    responseData?.detail ||
                    responseData?.message ||
                    responseData?.error ||
                    requestError?.message ||
                    "Unable to load the live examination monitor.";

                setError(String(detail));
            } finally {
                if (
                    mountedRef.current &&
                    requestId === requestSequenceRef.current
                ) {
                    setLoading(false);
                    setRefreshing(false);
                    inFlightRef.current = false;
                }
            }
        },
        [subject, selectedDate, lastUpdated]
    );

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            requestSequenceRef.current += 1;
            inFlightRef.current = false;
        };
    }, []);

    useEffect(() => {
        selectedApplicantKeyRef.current = selectedApplicantKey;
    }, [selectedApplicantKey]);

    useEffect(() => {
        setLastUpdated(null);
        setLoading(true);
        setError("");

        loadMonitorData();

        return () => {
            requestSequenceRef.current += 1;
        };
    }, [subject, selectedDate]); // Load the selected subject and date.

    useEffect(() => {
        if (!autoRefresh) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            loadMonitorData();
        }, REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
    }, [autoRefresh, loadMonitorData]);

    const visibleApplicants = useMemo(() => {
        const query = normalizeText(search);

        const filtered = leaderboard.filter((applicant) => {
            if (!query) {
                return true;
            }

            const name = normalizeText(getApplicantName(applicant));
            const applicationNumber = normalizeText(
                getApplicationNumber(applicant)
            );
            const status = normalizeText(getApplicantStatus(applicant));

            return (
                name.includes(query) ||
                applicationNumber.includes(query) ||
                status.includes(query)
            );
        });

        return [...filtered].sort((a, b) => {
            const aStats = getApplicantStats(a);
            const bStats = getApplicantStats(b);

            if (bStats.scorePercentage !== aStats.scorePercentage) {
                return bStats.scorePercentage - aStats.scorePercentage;
            }

            if (bStats.correctAnswers !== aStats.correctAnswers) {
                return bStats.correctAnswers - aStats.correctAnswers;
            }

            return aStats.questionsAnswered - bStats.questionsAnswered;
        });
    }, [leaderboard, search]);

    const selectedApplicant = useMemo(() => {
        if (!selectedApplicantKey) {
            return visibleApplicants[0] || leaderboard[0] || null;
        }

        return (
            leaderboard.find(
                (applicant) =>
                    getApplicantKey(applicant) === selectedApplicantKey
            ) ||
            visibleApplicants[0] ||
            null
        );
    }, [leaderboard, visibleApplicants, selectedApplicantKey]);

    const selectedStats = useMemo(
        () =>
            selectedApplicant
                ? getApplicantStats(selectedApplicant)
                : {
                      answers: [],
                      questionsAnswered: 0,
                      correctAnswers: 0,
                      wrongAnswers: 0,
                      totalQuestions: 0,
                      unansweredQuestions: 0,
                      obtainedMarks: 0,
                      totalMarks: 0,
                      scorePercentage: 0,
                      accuracy: 0,
                      completion: 0,
                  },
        [selectedApplicant]
    );

    const currentlySitting = useMemo(() => {
        if (monitorData.currently_sitting !== null) {
            return monitorData.currently_sitting;
        }

        return leaderboard.filter((applicant) => {
            const status = getApplicantStatus(applicant);

            return (
                status === "in_progress" &&
                numberValue(applicant?.remaining_seconds) > 0
            );
        }).length;
    }, [monitorData.currently_sitting, leaderboard]);

    const completedCount = useMemo(
        () =>
            leaderboard.filter((applicant) => {
                const status = getApplicantStatus(applicant);

                return status === "submitted" || status === "expired";
            }).length,
        [leaderboard]
    );

    const averageScore = useMemo(() => {
        if (leaderboard.length === 0) {
            return 0;
        }

        const total = leaderboard.reduce(
            (sum, applicant) =>
                sum + getApplicantStats(applicant).scorePercentage,
            0
        );

        return total / leaderboard.length;
    }, [leaderboard]);

    const handleSelectApplicant = useCallback((applicant) => {
        const key = getApplicantKey(applicant);

        selectedApplicantKeyRef.current = key;
        setSelectedApplicantKey(key);
    }, []);

    const handleRefresh = useCallback(() => {
        loadMonitorData({ manual: true });
    }, [loadMonitorData]);

    const subjectLabel =
        SUBJECTS.find((item) => item.value === subject)?.label || subject;

    const handleSubjectChange = (event) => {
        setSubject(event.target.value);
        setSelectedApplicantKey(null);
        selectedApplicantKeyRef.current = null;
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
        setSelectedApplicantKey(null);
        selectedApplicantKeyRef.current = null;
    };

    const panelSx = {
        border: `1px solid ${COLORS.border}`,
        borderRadius: 3,
        boxShadow: "0 3px 14px rgba(20, 40, 63, 0.035)",
        background: COLORS.white,
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: COLORS.background,
                p: { xs: 1.5, sm: 2.5, md: 3 },
                color: COLORS.text,
            }}
        >
            <Box sx={{ maxWidth: 1800, mx: "auto" }}>
                {/* Page header */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        mb: 2.5,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Box
                        sx={{
                            background: `linear-gradient(120deg, ${COLORS.navy}, ${COLORS.navyLight})`,
                            px: { xs: 2, sm: 3, md: 3.5 },
                            py: { xs: 2.5, sm: 3 },
                            color: COLORS.white,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                right: -30,
                                top: -65,
                                width: 220,
                                height: 220,
                                borderRadius: "50%",
                                border: "35px solid rgba(255,255,255,0.05)",
                                pointerEvents: "none",
                            }}
                        />

                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", md: "center" }}
                            spacing={2}
                            sx={{ position: "relative", zIndex: 1 }}
                        >
                            <Box>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ mb: 1 }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: COLORS.green,
                                            boxShadow: `0 0 0 5px rgba(21,149,107,0.18)`,
                                        }}
                                    />

                                    <Typography
                                        variant="overline"
                                        sx={{
                                            letterSpacing: 2,
                                            fontWeight: 800,
                                            color: "#DCE8F3",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        PASSION AIDS THE FUTURE
                                    </Typography>
                                </Stack>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 900,
                                        letterSpacing: -0.6,
                                        fontSize: {
                                            xs: "1.7rem",
                                            sm: "2.1rem",
                                            md: "2.35rem",
                                        },
                                    }}
                                >
                                    Entrance Examination Live Monitor
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "rgba(255,255,255,0.76)",
                                        mt: 1,
                                        maxWidth: 700,
                                    }}
                                >
                                    Monitor applicants, saved answers,
                                    examination progress, scores, and
                                    performance rankings in real time.
                                </Typography>
                            </Box>

                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                            >
                                <Chip
                                    icon={
                                        <RadioButtonCheckedRounded
                                            sx={{
                                                color: autoRefresh
                                                    ? `${COLORS.green} !important`
                                                    : "#AAB5C3",
                                            }}
                                        />
                                    }
                                    label={
                                        autoRefresh
                                            ? "Live monitoring ON"
                                            : "Live monitoring PAUSED"
                                    }
                                    onClick={() =>
                                        setAutoRefresh((previous) => !previous)
                                    }
                                    sx={{
                                        color: COLORS.white,
                                        fontWeight: 700,
                                        bgcolor: "rgba(255,255,255,0.09)",
                                        border:
                                            "1px solid rgba(255,255,255,0.15)",
                                        cursor: "pointer",
                                    }}
                                />

                                <Tooltip title="Refresh monitor now">
                                    <span>
                                        <IconButton
                                            onClick={handleRefresh}
                                            disabled={refreshing}
                                            sx={{
                                                bgcolor:
                                                    "rgba(255,255,255,0.1)",
                                                color: COLORS.white,
                                                border:
                                                    "1px solid rgba(255,255,255,0.18)",
                                                "&:hover": {
                                                    bgcolor:
                                                        "rgba(255,255,255,0.2)",
                                                },
                                            }}
                                        >
                                            {refreshing ? (
                                                <CircularProgress
                                                    size={21}
                                                    sx={{ color: "white" }}
                                                />
                                            ) : (
                                                <RefreshRounded />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            bgcolor: COLORS.white,
                        }}
                    >
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} sm={5} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="monitor-subject-label">
                                        Examination subject
                                    </InputLabel>

                                    <Select
                                        labelId="monitor-subject-label"
                                        value={subject}
                                        label="Examination subject"
                                        onChange={handleSubjectChange}
                                    >
                                        {SUBJECTS.map((item) => (
                                            <MenuItem
                                                key={item.value}
                                                value={item.value}
                                            >
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Examination date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3} md={6}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="flex-end"
                                    spacing={1}
                                >
                                    <Chip
                                        icon={<QuizRounded />}
                                        label={subjectLabel}
                                        sx={{
                                            bgcolor: COLORS.blueLight,
                                            color: COLORS.blue,
                                            fontWeight: 700,
                                        }}
                                    />

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {lastUpdated
                                            ? `Last updated: ${formatTime(lastUpdated)}`
                                            : "Waiting for monitor data"}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>

                    {refreshing && (
                        <LinearProgress
                            sx={{
                                "& .MuiLinearProgress-bar": {
                                    bgcolor: COLORS.green,
                                },
                            }}
                        />
                    )}
                </Paper>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleRefresh}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Summary metrics */}
                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title="Examination applicants"
                            value={formatNumber(
                                Math.max(
                                    monitorData.total_applicants,
                                    leaderboard.length
                                )
                            )}
                            subtitle="Applicants returned for this subject and date"
                            icon={<GroupsRounded />}
                            color={COLORS.navy}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title="Currently sitting"
                            value={formatNumber(currentlySitting)}
                            subtitle="Applicants with examinations in progress"
                            icon={<RadioButtonCheckedRounded />}
                            color={COLORS.green}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title="Submitted / expired"
                            value={formatNumber(completedCount)}
                            subtitle="Attempts no longer in progress"
                            icon={<FactCheckRounded />}
                            color={COLORS.blue}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title="Average score"
                            value={`${formatNumber(averageScore, 1)}%`}
                            subtitle="Average percentage across returned applicants"
                            icon={<TrendingUpRounded />}
                            color={COLORS.orange}
                        />
                    </Grid>
                </Grid>

                {loading && leaderboard.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            ...panelSx,
                            p: 6,
                            textAlign: "center",
                        }}
                    >
                        <CircularProgress
                            sx={{ color: COLORS.green, mb: 2 }}
                        />

                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, color: COLORS.navy }}
                        >
                            Loading examination monitor
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            Retrieving applicants and examination activity...
                        </Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={2.5} alignItems="stretch">
                        {/* Applicants sidebar */}
                        <Grid item xs={12} md={4} lg={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    ...panelSx,
                                    height: { md: "100%" },
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ p: 2 }}>
                                    <SectionHeader
                                        icon={<GroupsRounded />}
                                        title="Applicants"
                                        subtitle={`${visibleApplicants.length} shown · ${leaderboard.length} total`}
                                    />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={search}
                                        onChange={(event) =>
                                            setSearch(event.target.value)
                                        }
                                        placeholder="Search name or application no."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRounded
                                                        sx={{
                                                            color: COLORS.muted,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Divider />

                                <Box
                                    sx={{
                                        maxHeight: { xs: 420, md: 680 },
                                        overflowY: "auto",
                                        p: 1,
                                    }}
                                >
                                    {visibleApplicants.length === 0 ? (
                                        <Box sx={{ p: 3, textAlign: "center" }}>
                                            <PersonRounded
                                                sx={{
                                                    color: COLORS.muted,
                                                    fontSize: 36,
                                                    mb: 1,
                                                }}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {leaderboard.length === 0
                                                    ? "No applicants were returned for this date and subject."
                                                    : "No applicants match your search."}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        visibleApplicants.map((applicant) => {
                                            const key =
                                                getApplicantKey(applicant);
                                            const isSelected =
                                                selectedApplicant &&
                                                getApplicantKey(
                                                    selectedApplicant
                                                ) === key;

                                            const stats =
                                                getApplicantStats(applicant);
                                            const status =
                                                getApplicantStatus(applicant);
                                            const appearance =
                                                getStatusAppearance(status);

                                            return (
                                                <Box
                                                    key={key}
                                                    onClick={() =>
                                                        handleSelectApplicant(
                                                            applicant
                                                        )
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(event) => {
                                                        if (
                                                            event.key ===
                                                                "Enter" ||
                                                            event.key === " "
                                                        ) {
                                                            event.preventDefault();
                                                            handleSelectApplicant(
                                                                applicant
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        p: 1.5,
                                                        mb: 0.75,
                                                        borderRadius: 2.5,
                                                        cursor: "pointer",
                                                        border: `1px solid ${
                                                            isSelected
                                                                ? COLORS.green
                                                                : "transparent"
                                                        }`,
                                                        bgcolor: isSelected
                                                            ? COLORS.greenLight
                                                            : "transparent",
                                                        transition:
                                                            "background 0.15s ease",
                                                        "&:hover": {
                                                            bgcolor: isSelected
                                                                ? COLORS.greenLight
                                                                : "#F6F8FA",
                                                        },
                                                        outline: "none",
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1.25}
                                                        alignItems="flex-start"
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 42,
                                                                height: 42,
                                                                bgcolor:
                                                                    isSelected
                                                                        ? COLORS.green
                                                                        : COLORS.navy,
                                                                color: "white",
                                                                fontWeight: 800,
                                                                fontSize: 14,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {getApplicantName(
                                                                applicant
                                                            )
                                                                .split(" ")
                                                                .filter(Boolean)
                                                                .slice(0, 2)
                                                                .map(
                                                                    (part) =>
                                                                        part[0]
                                                                )
                                                                .join("")
                                                                .toUpperCase()}
                                                        </Avatar>

                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    color: COLORS.navy,
                                                                    overflowWrap:
                                                                        "anywhere",
                                                                }}
                                                            >
                                                                {getApplicantName(
                                                                    applicant
                                                                )}
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    color: COLORS.muted,
                                                                    mt: 0.35,
                                                                    overflowWrap:
                                                                        "anywhere",
                                                                }}
                                                            >
                                                                {getApplicationNumber(
                                                                    applicant
                                                                )}
                                                            </Typography>

                                                            <Stack
                                                                direction="row"
                                                                spacing={0.75}
                                                                alignItems="center"
                                                                flexWrap="wrap"
                                                                useFlexGap
                                                                sx={{ mt: 1 }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        appearance.label
                                                                    }
                                                                    sx={{
                                                                        height: 23,
                                                                        fontSize: 10,
                                                                        fontWeight: 800,
                                                                        color: appearance.color,
                                                                        bgcolor: appearance.background,
                                                                    }}
                                                                />

                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        color: COLORS.text,
                                                                    }}
                                                                >
                                                                    {stats.questionsAnswered}
                                                                    /
                                                                    {stats.totalQuestions}
                                                                    {" "}answered
                                                                </Typography>
                                                            </Stack>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                textAlign: "right",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: COLORS.navy,
                                                                    fontWeight: 900,
                                                                }}
                                                            >
                                                                {formatNumber(
                                                                    stats.scorePercentage,
                                                                    1
                                                                )}
                                                                %
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: COLORS.muted,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {stats.correctAnswers}
                                                                {" "}correct
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Selected applicant details */}
                        <Grid item xs={12} md={8} lg={9}>
                            <Stack spacing={2.5}>
                                <Paper elevation={0} sx={{ ...panelSx, p: 2.5 }}>
                                    <SectionHeader
                                        icon={<PersonRounded />}
                                        title="Selected Applicant"
                                        subtitle="Applicant profile and live examination performance"
                                        action={
                                            selectedApplicant ? (
                                                <Chip
                                                    icon={<VisibilityRounded />}
                                                    label="Live details"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: COLORS.green,
                                                        bgcolor: COLORS.greenLight,
                                                    }}
                                                />
                                            ) : null
                                        }
                                    />

                                    {!selectedApplicant ? (
                                        <Alert severity="info">
                                            Select an applicant from the
                                            sidebar to view their profile,
                                            answers, and performance.
                                        </Alert>
                                    ) : (
                                        <>
                                            <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                <Grid item xs={12} sm={7}>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1.75}
                                                        alignItems="center"
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 62,
                                                                height: 62,
                                                                bgcolor: COLORS.navy,
                                                                fontSize: 22,
                                                                fontWeight: 900,
                                                            }}
                                                        >
                                                            {getApplicantName(
                                                                selectedApplicant
                                                            )
                                                                .split(" ")
                                                                .filter(Boolean)
                                                                .slice(0, 2)
                                                                .map(
                                                                    (part) =>
                                                                        part[0]
                                                                )
                                                                .join("")
                                                                .toUpperCase()}
                                                        </Avatar>

                                                        <Box
                                                            sx={{
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    color: COLORS.navy,
                                                                    overflowWrap:
                                                                        "anywhere",
                                                                }}
                                                            >
                                                                {getApplicantName(
                                                                    selectedApplicant
                                                                )}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: COLORS.muted,
                                                                    mt: 0.5,
                                                                    overflowWrap:
                                                                        "anywhere",
                                                                }}
                                                            >
                                                                {getApplicationNumber(
                                                                    selectedApplicant
                                                                )}
                                                            </Typography>

                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                flexWrap="wrap"
                                                                useFlexGap
                                                                sx={{ mt: 1 }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        subjectLabel
                                                                    }
                                                                    sx={{
                                                                        bgcolor: COLORS.blueLight,
                                                                        color: COLORS.blue,
                                                                        fontWeight: 800,
                                                                    }}
                                                                />

                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        getStatusAppearance(
                                                                            getApplicantStatus(
                                                                                selectedApplicant
                                                                            )
                                                                        ).label
                                                                    }
                                                                    sx={{
                                                                        bgcolor:
                                                                            getStatusAppearance(
                                                                                getApplicantStatus(
                                                                                    selectedApplicant
                                                                                )
                                                                            ).background,
                                                                        color:
                                                                            getStatusAppearance(
                                                                                getApplicantStatus(
                                                                                    selectedApplicant
                                                                                )
                                                                            ).color,
                                                                        fontWeight: 800,
                                                                    }}
                                                                />
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </Grid>

                                                <Grid item xs={12} sm={5}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 2.5,
                                                            bgcolor: "#F7F9FC",
                                                            border: `1px solid ${COLORS.border}`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: COLORS.muted,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            CURRENT SCORE
                                                        </Typography>

                                                        <Stack
                                                            direction="row"
                                                            alignItems="baseline"
                                                            spacing={0.75}
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            <Typography
                                                                variant="h3"
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    color: COLORS.navy,
                                                                }}
                                                            >
                                                                {formatNumber(
                                                                    selectedStats.obtainedMarks,
                                                                    1
                                                                )}
                                                            </Typography>

                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: COLORS.muted,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                /{" "}
                                                                {formatNumber(
                                                                    selectedStats.totalQuestions,
                                                                    1
                                                                )}
                                                            </Typography>

                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: COLORS.green,
                                                                    fontWeight: 900,
                                                                    ml: "auto !important",
                                                                }}
                                                            >
                                                                {formatNumber(
                                                                    selectedStats.scorePercentage,
                                                                    1
                                                                )}
                                                                %
                                                            </Typography>
                                                        </Stack>

                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.max(
                                                                0,
                                                                Math.min(
                                                                    100,
                                                                    selectedStats.scorePercentage
                                                                )
                                                            )}
                                                            sx={{
                                                                height: 7,
                                                                borderRadius: 10,
                                                                mt: 1.5,
                                                                bgcolor: "#E5EAF0",
                                                                "& .MuiLinearProgress-bar":
                                                                    {
                                                                        borderRadius: 10,
                                                                        bgcolor: COLORS.green,
                                                                    },
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            <Divider sx={{ my: 2.5 }} />

                                            <Grid container spacing={1.5}>
                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Answered"
                                                        value={`${selectedStats.questionsAnswered}/${selectedStats.totalQuestions}`}
                                                        subtitle="Questions answered"
                                                        icon={<QuizRounded />}
                                                        color={COLORS.blue}
                                                    />
                                                </Grid>

                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Correct"
                                                        value={formatNumber(
                                                            selectedStats.correctAnswers
                                                        )}
                                                        subtitle="Correct answers"
                                                        icon={<CheckCircleRounded />}
                                                        color={COLORS.green}
                                                    />
                                                </Grid>

                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Wrong"
                                                        value={formatNumber(
                                                            selectedStats.wrongAnswers
                                                        )}
                                                        subtitle="Incorrect answers"
                                                        icon={<CancelRounded />}
                                                        color={COLORS.red}
                                                    />
                                                </Grid>

                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Unanswered"
                                                        value={formatNumber(
                                                            selectedStats.unansweredQuestions
                                                        )}
                                                        subtitle="Questions remaining"
                                                        icon={<HourglassBottomRounded />}
                                                        color={COLORS.orange}
                                                    />
                                                </Grid>

                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Accuracy"
                                                        value={`${formatNumber(
                                                            selectedStats.accuracy,
                                                            1
                                                        )}%`}
                                                        subtitle="Correct / answered"
                                                        icon={<TrendingUpRounded />}
                                                        color={COLORS.green}
                                                    />
                                                </Grid>

                                                <Grid item xs={6} sm={4} lg={2}>
                                                    <MetricCard
                                                        title="Completion"
                                                        value={`${formatNumber(
                                                            selectedStats.completion,
                                                            1
                                                        )}%`}
                                                        subtitle="Exam progress"
                                                        icon={<SignalCellularAltRounded />}
                                                        color={COLORS.navy}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <Grid
                                                container
                                                spacing={1.5}
                                                sx={{ mt: 1 }}
                                            >
                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            border: `1px solid ${COLORS.border}`,
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: COLORS.muted,
                                                            }}
                                                        >
                                                            Examination started
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {formatDateTime(
                                                                firstAvailable(
                                                                    selectedApplicant.started_at,
                                                                    selectedApplicant.start_time,
                                                                    selectedApplicant.attempt?.started_at
                                                                )
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            border: `1px solid ${COLORS.border}`,
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: COLORS.muted,
                                                            }}
                                                        >
                                                            Last activity
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {formatDateTime(
                                                                firstAvailable(
                                                                    selectedApplicant.last_answered_at,
                                                                    selectedApplicant.last_activity_at,
                                                                    selectedApplicant.updated_at
                                                                )
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            border: `1px solid ${COLORS.border}`,
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: COLORS.muted,
                                                            }}
                                                        >
                                                            Examination expires
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {formatDateTime(
                                                                firstAvailable(
                                                                    selectedApplicant.expires_at,
                                                                    selectedApplicant.expiry_time,
                                                                    selectedApplicant.attempt?.expires_at
                                                                )
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            border: `1px solid ${COLORS.border}`,
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: COLORS.muted,
                                                            }}
                                                        >
                                                            Time remaining
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 800,
                                                                mt: 0.5,
                                                                color:
                                                                    numberValue(
                                                                        selectedApplicant.remaining_seconds
                                                                    ) <= 300 &&
                                                                    getApplicantStatus(
                                                                        selectedApplicant
                                                                    ) ===
                                                                        "in_progress"
                                                                        ? COLORS.red
                                                                        : COLORS.navy,
                                                            }}
                                                        >
                                                            {selectedApplicant.remaining_seconds !==
                                                            undefined
                                                                ? formatDuration(
                                                                      selectedApplicant.remaining_seconds
                                                                  )
                                                                : "Not available"}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Paper>

                                {/* Answer details */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        ...panelSx,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box sx={{ p: 2.5 }}>
                                        <SectionHeader
                                            icon={<FactCheckRounded />}
                                            title="Applicant Answer Details"
                                            subtitle={
                                                selectedApplicant
                                                    ? `${selectedStats.answers.length} answer records returned for ${getApplicantName(selectedApplicant)}`
                                                    : "Select an applicant to inspect their answers"
                                            }
                                        />

                                        {!selectedApplicant ? (
                                            <Alert severity="info">
                                                Select an applicant to view
                                                their answer details.
                                            </Alert>
                                        ) : selectedStats.answers.length ===
                                          0 ? (
                                            <Alert
                                                severity="info"
                                                sx={{ borderRadius: 2 }}
                                            >
                                                No answer records were returned
                                                for this applicant yet. If the
                                                applicant has answered
                                                questions, make sure the backend
                                                includes the saved answers in
                                                the monitor response.
                                            </Alert>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "1fr",
                                                        lg: "repeat(2, minmax(0, 1fr))",
                                                    },
                                                    gap: 1.5,
                                                }}
                                            >
                                                {selectedStats.answers.map(
                                                    (answer, index) => {
                                                        const selected =
                                                            getSelectedAnswer(
                                                                answer
                                                            );
                                                        const correct =
                                                            getCorrectAnswer(
                                                                answer
                                                            );
                                                        const correctness =
                                                            getAnswerCorrectness(
                                                                answer
                                                            );

                                                        const options =
                                                            answer?.options ||
                                                            answer?.question
                                                                ?.options ||
                                                            null;

                                                        const questionNumber =
                                                            getQuestionNumber(
                                                                answer,
                                                                index
                                                            );

                                                        return (
                                                            <Box
                                                                key={`${questionNumber}-${index}`}
                                                                sx={{
                                                                    border: `1px solid ${COLORS.border}`,
                                                                    borderRadius: 2.5,
                                                                    p: 2,
                                                                    minWidth: 0,
                                                                    bgcolor:
                                                                        correctness ===
                                                                        true
                                                                            ? "#F7FCF9"
                                                                            : correctness ===
                                                                                false
                                                                              ? "#FFF9F8"
                                                                              : COLORS.white,
                                                                }}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="flex-start"
                                                                    justifyContent="space-between"
                                                                    spacing={1}
                                                                    sx={{
                                                                        mb: 1.25,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{
                                                                            fontWeight: 900,
                                                                            color: COLORS.navy,
                                                                        }}
                                                                    >
                                                                        Question{" "}
                                                                        {
                                                                            questionNumber
                                                                        }
                                                                    </Typography>

                                                                    <Chip
                                                                        size="small"
                                                                        icon={
                                                                            correctness ===
                                                                            true ? (
                                                                                <CheckCircleRounded />
                                                                            ) : correctness ===
                                                                              false ? (
                                                                                <CancelRounded />
                                                                            ) : (
                                                                                <VisibilityRounded />
                                                                            )
                                                                        }
                                                                        label={
                                                                            correctness ===
                                                                            true
                                                                                ? "Correct"
                                                                                : correctness ===
                                                                                  false
                                                                                  ? "Incorrect"
                                                                                  : "Unconfirmed"
                                                                        }
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            color:
                                                                                correctness ===
                                                                                true
                                                                                    ? COLORS.green
                                                                                    : correctness ===
                                                                                      false
                                                                                      ? COLORS.red
                                                                                      : COLORS.muted,
                                                                            bgcolor:
                                                                                correctness ===
                                                                                true
                                                                                    ? COLORS.greenLight
                                                                                    : correctness ===
                                                                                      false
                                                                                      ? COLORS.redLight
                                                                                      : "#F0F2F5",
                                                                        }}
                                                                    />
                                                                </Stack>

                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        lineHeight: 1.7,
                                                                        mb: 1.5,
                                                                        whiteSpace:
                                                                            "pre-wrap",
                                                                        overflowWrap:
                                                                            "anywhere",
                                                                    }}
                                                                >
                                                                    {getQuestionText(
                                                                        answer
                                                                    )}
                                                                </Typography>

                                                                {options &&
                                                                    typeof options ===
                                                                        "object" && (
                                                                        <Stack
                                                                            spacing={
                                                                                0.75
                                                                            }
                                                                            sx={{
                                                                                mb: 1.5,
                                                                            }}
                                                                        >
                                                                            {(
                                                                                Array.isArray(
                                                                                    options
                                                                                )
                                                                                    ? options
                                                                                    : Object.entries(
                                                                                          options
                                                                                      ).map(
                                                                                          ([
                                                                                              key,
                                                                                              value,
                                                                                          ]) => ({
                                                                                              key,
                                                                                              value,
                                                                                          })
                                                                                      )
                                                                            ).map(
                                                                                (
                                                                                    option,
                                                                                    optionIndex
                                                                                ) => {
                                                                                    const key =
                                                                                        typeof option ===
                                                                                        "object"
                                                                                            ? firstAvailable(
                                                                                                  option?.key,
                                                                                                  option?.label,
                                                                                                  option?.letter,
                                                                                                  option?.option
                                                                                              ) ??
                                                                                              ANSWER_OPTIONS[
                                                                                                  optionIndex
                                                                                              ]
                                                                                            : ANSWER_OPTIONS[
                                                                                                  optionIndex
                                                                                              ];

                                                                                    const value =
                                                                                        typeof option ===
                                                                                        "object"
                                                                                            ? firstAvailable(
                                                                                                  option?.text,
                                                                                                  option?.value,
                                                                                                  option?.content,
                                                                                                  option?.label,
                                                                                                  option?.option_text
                                                                                              ) ??
                                                                                              String(
                                                                                                  option
                                                                                              )
                                                                                            : option;

                                                                                    const isSelected =
                                                                                        normalizeText(
                                                                                            selected
                                                                                        ) ===
                                                                                            normalizeText(
                                                                                                key
                                                                                            ) ||
                                                                                        normalizeText(
                                                                                            selected
                                                                                        ) ===
                                                                                            normalizeText(
                                                                                                value
                                                                                            );

                                                                                    const isCorrect =
                                                                                        normalizeText(
                                                                                            correct
                                                                                        ) ===
                                                                                            normalizeText(
                                                                                                key
                                                                                            ) ||
                                                                                        normalizeText(
                                                                                            correct
                                                                                        ) ===
                                                                                            normalizeText(
                                                                                                value
                                                                                            );

                                                                                    return (
                                                                                        <Box
                                                                                            key={`${key}-${optionIndex}`}
                                                                                            sx={{
                                                                                                display:
                                                                                                    "flex",
                                                                                                gap: 1,
                                                                                                alignItems:
                                                                                                    "flex-start",
                                                                                                p: 1,
                                                                                                borderRadius: 1.5,
                                                                                                border: `1px solid ${
                                                                                                    isCorrect
                                                                                                        ? COLORS.green
                                                                                                        : isSelected
                                                                                                          ? COLORS.red
                                                                                                          : COLORS.border
                                                                                                }`,
                                                                                                bgcolor:
                                                                                                    isCorrect
                                                                                                        ? COLORS.greenLight
                                                                                                        : isSelected
                                                                                                          ? COLORS.redLight
                                                                                                          : COLORS.white,
                                                                                            }}
                                                                                        >
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    fontWeight: 900,
                                                                                                    color:
                                                                                                        isCorrect
                                                                                                            ? COLORS.green
                                                                                                            : isSelected
                                                                                                              ? COLORS.red
                                                                                                              : COLORS.muted,
                                                                                                    minWidth: 18,
                                                                                                }}
                                                                                            >
                                                                                                {key}
                                                                                            </Typography>

                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                sx={{
                                                                                                    flex: 1,
                                                                                                    overflowWrap:
                                                                                                        "anywhere",
                                                                                                }}
                                                                                            >
                                                                                                {String(
                                                                                                    value
                                                                                                )}
                                                                                            </Typography>

                                                                                            {isSelected && (
                                                                                                <Chip
                                                                                                    size="small"
                                                                                                    label="Selected"
                                                                                                    sx={{
                                                                                                        height: 21,
                                                                                                        fontSize: 10,
                                                                                                        fontWeight: 800,
                                                                                                    }}
                                                                                                />
                                                                                            )}

                                                                                            {isCorrect && (
                                                                                                <CheckCircleRounded
                                                                                                    sx={{
                                                                                                        color: COLORS.green,
                                                                                                        fontSize: 18,
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        </Box>
                                                                                    );
                                                                                }
                                                                            )}
                                                                        </Stack>
                                                                    )}

                                                                <Grid
                                                                    container
                                                                    spacing={1}
                                                                >
                                                                    <Grid
                                                                        item
                                                                        xs={6}
                                                                    >
                                                                        <AnswerValue
                                                                            label="Applicant's answer"
                                                                            value={
                                                                                selected
                                                                            }
                                                                            correct={
                                                                                correctness
                                                                            }
                                                                        />
                                                                    </Grid>

                                                                    <Grid
                                                                        item
                                                                        xs={6}
                                                                    >
                                                                        <AnswerValue
                                                                            label="Correct answer"
                                                                            value={
                                                                                correct
                                                                            }
                                                                            correct={
                                                                                true
                                                                            }
                                                                            muted
                                                                        />
                                                                    </Grid>
                                                                </Grid>

                                                                <Stack
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                    alignItems="center"
                                                                    sx={{
                                                                        mt: 1.25,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        Marks awarded
                                                                    </Typography>

                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 900,
                                                                            color: COLORS.navy,
                                                                        }}
                                                                    >
                                                                        {formatNumber(
                                                                            getAnswerMarks(
                                                                                answer
                                                                            ),
                                                                            1
                                                                        )}
                                                                    </Typography>
                                                                </Stack>
                                                            </Box>
                                                        );
                                                    }
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </Stack>
                        </Grid>

                        {/* Full-width performance ranking */}
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    ...panelSx,
                                    overflow: "hidden",
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ p: 2.5 }}>
                                    <SectionHeader
                                        icon={<EmojiEventsRounded />}
                                        title="Performance Ranking"
                                        subtitle={`All applicants · ${subjectLabel} · ${selectedDate}`}
                                        action={
                                            <Chip
                                                label={`${visibleApplicants.length} applicants displayed`}
                                                sx={{
                                                    bgcolor: COLORS.orangeLight,
                                                    color: COLORS.orange,
                                                    fontWeight: 800,
                                                }}
                                            />
                                        }
                                    />

                                    <Alert
                                        severity="info"
                                        icon={<TrendingUpRounded />}
                                        sx={{
                                            mb: 2,
                                            borderRadius: 2,
                                            "& .MuiAlert-message": {
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        Rankings are ordered by score
                                        percentage, then correct answers.
                                        Select any row to update the selected
                                        applicant panel and answer details.
                                    </Alert>
                                </Box>

                                <TableContainer
                                    sx={{
                                        width: "100%",
                                        maxHeight: 620,
                                        overflowX: "auto",
                                        overflowY: "auto",
                                        "& table": {
                                            minWidth: 1150,
                                        },
                                    }}
                                >
                                    <Table
                                        stickyHeader
                                        size="small"
                                        aria-label="Entrance examination performance ranking"
                                    >
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    "Rank",
                                                    "Applicant",
                                                    "Application Number",
                                                    "Status",
                                                    "Answered",
                                                    "Correct",
                                                    "Wrong",
                                                    "Unanswered",
                                                    "Marks",
                                                    "Score",
                                                    "Progress",
                                                    "Time Remaining",
                                                ].map((heading) => (
                                                    <TableCell
                                                        key={heading}
                                                        sx={{
                                                            bgcolor: COLORS.navy,
                                                            color: COLORS.white,
                                                            fontWeight: 800,
                                                            whiteSpace: "nowrap",
                                                            py: 1.75,
                                                            borderBottom: `2px solid ${COLORS.gold}`,
                                                        }}
                                                    >
                                                        {heading}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {visibleApplicants.map(
                                                (applicant, index) => {
                                                    const key =
                                                        getApplicantKey(
                                                            applicant
                                                        );
                                                    const stats =
                                                        getApplicantStats(
                                                            applicant
                                                        );
                                                    const status =
                                                        getApplicantStatus(
                                                            applicant
                                                        );
                                                    const appearance =
                                                        getStatusAppearance(
                                                            status
                                                        );
                                                    const isSelected =
                                                        selectedApplicant &&
                                                        getApplicantKey(
                                                            selectedApplicant
                                                        ) === key;

                                                    return (
                                                        <TableRow
                                                            hover
                                                            key={key}
                                                            selected={
                                                                Boolean(
                                                                    isSelected
                                                                )
                                                            }
                                                            onClick={() =>
                                                                handleSelectApplicant(
                                                                    applicant
                                                                )
                                                            }
                                                            sx={{
                                                                cursor: "pointer",
                                                                "&:nth-of-type(even)":
                                                                    {
                                                                        bgcolor:
                                                                            isSelected
                                                                                ? COLORS.greenLight
                                                                                : "#FAFBFC",
                                                                    },
                                                                "&.Mui-selected":
                                                                    {
                                                                        bgcolor: `${COLORS.greenLight} !important`,
                                                                    },
                                                                "&.Mui-selected:hover":
                                                                    {
                                                                        bgcolor: "#DDF3E9 !important",
                                                                    },
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    width: 110,
                                                                    minWidth: 110,
                                                                }}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                >
                                                                    <Avatar
                                                                        sx={{
                                                                            width: 30,
                                                                            height: 30,
                                                                            fontSize: 12,
                                                                            fontWeight: 900,
                                                                            bgcolor:
                                                                                index ===
                                                                                0
                                                                                    ? COLORS.gold
                                                                                    : index ===
                                                                                        1
                                                                                      ? "#A8B2BF"
                                                                                      : index ===
                                                                                          2
                                                                                        ? "#C58A5B"
                                                                                        : "#EDF1F5",
                                                                            color:
                                                                                index <
                                                                                3
                                                                                    ? COLORS.navy
                                                                                    : COLORS.text,
                                                                        }}
                                                                    >
                                                                        {index +
                                                                            1}
                                                                    </Avatar>

                                                                    {index ===
                                                                        0 && (
                                                                        <EmojiEventsRounded
                                                                            sx={{
                                                                                color: COLORS.gold,
                                                                                fontSize: 20,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 200,
                                                                    maxWidth: 280,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        color: COLORS.navy,
                                                                        overflowWrap:
                                                                            "anywhere",
                                                                    }}
                                                                >
                                                                    {getApplicantName(
                                                                        applicant
                                                                    )}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 190,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: COLORS.muted,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {getApplicationNumber(
                                                                        applicant
                                                                    )}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 130,
                                                                }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        appearance.label
                                                                    }
                                                                    sx={{
                                                                        bgcolor:
                                                                            appearance.background,
                                                                        color: appearance.color,
                                                                        fontWeight: 800,
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 115,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {
                                                                    stats.questionsAnswered
                                                                }
                                                                {" / "}
                                                                {
                                                                    stats.totalQuestions
                                                                }
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 85,
                                                                    color: COLORS.green,
                                                                    fontWeight: 900,
                                                                }}
                                                            >
                                                                {
                                                                    stats.correctAnswers
                                                                }
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 75,
                                                                    color: COLORS.red,
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {
                                                                    stats.wrongAnswers
                                                                }
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 100,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {
                                                                    stats.unansweredQuestions
                                                                }
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 120,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {formatNumber(
                                                                    stats.obtainedMarks,
                                                                    1
                                                                )}
                                                                {" / "}
                                                                {formatNumber(
                                                                    stats.totalMarks,
                                                                    1
                                                                )}
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 110,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 900,
                                                                        color:
                                                                            stats.scorePercentage >=
                                                                            50
                                                                                ? COLORS.green
                                                                                : COLORS.red,
                                                                    }}
                                                                >
                                                                    {formatNumber(
                                                                        stats.scorePercentage,
                                                                        1
                                                                    )}
                                                                    %
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 150,
                                                                }}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                >
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={Math.max(
                                                                            0,
                                                                            Math.min(
                                                                                100,
                                                                                stats.completion
                                                                            )
                                                                        )}
                                                                        sx={{
                                                                            flex: 1,
                                                                            minWidth: 55,
                                                                            height: 7,
                                                                            borderRadius: 5,
                                                                            bgcolor:
                                                                                "#E8EDF2",
                                                                            "& .MuiLinearProgress-bar":
                                                                                {
                                                                                    bgcolor: COLORS.green,
                                                                                    borderRadius: 5,
                                                                                },
                                                                        }}
                                                                    />

                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            minWidth: 40,
                                                                            textAlign:
                                                                                "right",
                                                                        }}
                                                                    >
                                                                        {formatNumber(
                                                                            stats.completion,
                                                                            0
                                                                        )}
                                                                        %
                                                                    </Typography>
                                                                </Stack>
                                                            </TableCell>

                                                            <TableCell
                                                                sx={{
                                                                    minWidth: 145,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    color:
                                                                        numberValue(
                                                                            applicant.remaining_seconds
                                                                        ) <= 300 &&
                                                                        status ===
                                                                            "in_progress"
                                                                            ? COLORS.red
                                                                            : COLORS.text,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {applicant.remaining_seconds !==
                                                                undefined
                                                                    ? formatDuration(
                                                                          applicant.remaining_seconds
                                                                      )
                                                                    : "—"}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            )}

                                            {visibleApplicants.length ===
                                                0 && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={12}
                                                        align="center"
                                                        sx={{ py: 6 }}
                                                    >
                                                        <GroupsRounded
                                                            sx={{
                                                                fontSize: 42,
                                                                color: COLORS.muted,
                                                                mb: 1,
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 800,
                                                                color: COLORS.navy,
                                                            }}
                                                        >
                                                            No ranking data
                                                            available
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            Try another
                                                            examination date or
                                                            subject, or refresh
                                                            the monitor.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box
                                    sx={{
                                        p: 2,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        bgcolor: "#FAFBFC",
                                    }}
                                >
                                    <Stack
                                        direction={{
                                            xs: "column",
                                            sm: "row",
                                        }}
                                        justifyContent="space-between"
                                        spacing={1}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Showing {visibleApplicants.length}{" "}
                                            of {leaderboard.length} applicants.
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Auto-refresh:{" "}
                                            {autoRefresh
                                                ? "Every 5 seconds"
                                                : "Paused"}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Footer */}
                <Box
                    sx={{
                        mt: 3,
                        py: 2,
                        textAlign: "center",
                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: COLORS.muted,
                            letterSpacing: 0.2,
                        }}
                    >
                        Design by Polu Integrated School Management System
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

